import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Wrench, Eraser, Undo2, ImageOff, Wand2, CopyCheck, Trash2,
  RefreshCcw, Unlink, Loader2, ChevronDown, ChevronUp, TextSearch,
} from "lucide-react";
import { api } from "@/lib/api";
import { appConfirm } from "@/components/AppDialogHost";

export const MetadataTools = ({ sessionId, album, track, onChanged, openSignal }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    if (openSignal) setOpen(true);
  }, [openSignal]);

  const run = async (key, { confirm, danger, fn, ok }) => {
    if (confirm && !(await appConfirm(confirm, { title: "Metadata Tools", danger }))) return;
    setBusy(key);
    try {
      const msg = await fn();
      toast.success(ok ? ok(msg) : "Done");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    }
    setBusy("");
  };

  const B = ({ id, icon: Icon, label, disabled, title, ...opts }) => (
    <button onClick={() => run(id, opts)} disabled={!!busy || disabled} title={title}
      data-testid={`rip-tool-${id}`}
      className="inline-flex items-center gap-1.5 rounded-md border border-line bg-bg-elevated px-2.5 py-1.5 text-[11px] text-ink-secondary hover:text-ink-primary hover:border-brand-amber/50 transition disabled:opacity-35">
      {busy === id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Icon className="h-3 w-3" />}
      {label}
    </button>
  );

  const group = (label, children) => (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-wider text-ink-muted mb-1.5">{label}</div>
      <div className="flex flex-wrap gap-1.5">{children}</div>
    </div>
  );

  return (
    <div className="rounded-xl border border-line bg-bg-surface" data-testid="rip-metadata-tools">
      <button onClick={() => setOpen((o) => !o)} data-testid="rip-tools-toggle"
        className="w-full flex items-center justify-between px-4 py-2.5 text-xs font-mono uppercase tracking-wider text-ink-muted hover:text-ink-primary transition">
        <span className="inline-flex items-center gap-2"><Wrench className="h-3.5 w-3.5 text-brand-amber" /> Metadata Cleaner &amp; Repair</span>
        {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3">
          <div className="text-[10px] text-ink-muted font-mono">
            Clearing only resets the editable tags — audio files are never deleted, and the original tags are kept for Restore.
          </div>

          {group("Selected track", [
            <B key="ct" id="clear-track" icon={Eraser} label="Clear track metadata" disabled={!track}
              title={track ? "" : "Select a track first"}
              confirm={track && `Clear all metadata on "${track.title || track.filename}"? The audio file is untouched and you can Restore later.`}
              danger
              fn={async () => (await api.post("/rip/meta/clear", { scope: "track", id: track.track_id })).data}
              ok={() => "Track metadata cleared (original saved for restore)"} />,
            <B key="rt" id="restore-track" icon={Undo2} label="Restore original" disabled={!track}
              fn={async () => (await api.post("/rip/meta/restore", { scope: "track", id: track.track_id })).data}
              ok={() => "Original track metadata restored"} />,
            <B key="ca" id="clear-art-track" icon={ImageOff} label="Remove artwork" disabled={!track}
              fn={async () => (await api.post("/rip/meta/clear", { scope: "track", id: track.track_id, what: "artwork" })).data}
              ok={() => "Artwork removed from this track"} />,
          ])}

          {group("Selected album", [
            <B key="cal" id="clear-album" icon={Eraser} label="Clear album metadata" disabled={!album}
              title={album ? "" : "Select an album in the strip above"}
              confirm={album && `Clear metadata on all ${album.track_count} tracks of "${album.title}"? Audio files are untouched.`}
              danger
              fn={async () => (await api.post("/rip/meta/clear", { scope: "album", id: album.album_id })).data}
              ok={(d) => `Cleared metadata on ${d.affected} tracks`} />,
            <B key="ral" id="restore-album" icon={Undo2} label="Restore original" disabled={!album}
              fn={async () => (await api.post("/rip/meta/restore", { scope: "album", id: album.album_id })).data}
              ok={(d) => `Restored ${d.restored} tracks`} />,
            <B key="rmb" id="reset-mb" icon={Unlink} label="Reset MusicBrainz match" disabled={!album}
              confirm={album && `Remove the MusicBrainz match and artwork from "${album.title}"? Titles stay as they are.`}
              fn={async () => (await api.post(`/rip/albums/${album.album_id}/reset-mb`)).data}
              ok={(d) => `MusicBrainz match reset on ${d.affected} tracks`} />,
            <B key="smb" id="resync-mb" icon={RefreshCcw} label="Re-sync MusicBrainz" disabled={!album || !album.mb_release_id}
              title={album && !album.mb_release_id ? "No MusicBrainz match yet" : ""}
              fn={async () => (await api.post(`/rip/albums/${album.album_id}/resync-mb`)).data}
              ok={(d) => `Re-synced "${d.release}" — ${d.updated} tracks updated`} />,
            <B key="ad" id="autodetect" icon={Wand2} label="Auto-detect genre & year" disabled={!album}
              fn={async () => (await api.post(`/rip/albums/${album.album_id}/autodetect`, { what: "both" })).data}
              ok={(d) => `Detected ${[d.genre, d.year].filter(Boolean).join(" · ")} → ${d.affected} tracks`} />,
            <B key="aa" id="clear-art-album" icon={ImageOff} label="Remove artwork" disabled={!album}
              fn={async () => (await api.post("/rip/meta/clear", { scope: "album", id: album.album_id, what: "artwork" })).data}
              ok={(d) => `Artwork removed from ${d.affected} tracks`} />,
          ])}

          {group("Whole session", [
            <B key="fm" id="fix-missing" icon={TextSearch} label="Fix missing artist / titles"
              fn={async () => (await api.post("/rip/meta/fix-missing",
                album ? { album_id: album.album_id } : { session_id: sessionId })).data}
              ok={(d) => d.fixed ? `Filled in ${d.fixed} tracks from filenames` : "Nothing to fix — filenames didn't reveal more info"} />,
            <B key="dup" id="scan-dups" icon={CopyCheck} label="Detect duplicates"
              fn={async () => (await api.post(`/rip/sessions/${sessionId}/scan-duplicates`)).data}
              ok={(d) => d.flagged ? `${d.flagged} possible duplicates flagged` : "No duplicates found"} />,
            <B key="cw" id="clear-workspace" icon={Trash2} label="Clear workspace"
              confirm="Remove ALL tracks in this session that aren't in the Music Library yet? Their uploaded files are deleted. Library tracks are kept."
              danger
              fn={async () => (await api.post(`/rip/sessions/${sessionId}/clear-workspace`)).data}
              ok={(d) => `Workspace cleared — ${d.removed} tracks removed`} />,
          ])}
        </div>
      )}
    </div>
  );
};
